import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'

export const LanguageContext = createContext()

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

const SUPPORTED_LANGUAGES = ['ar', 'en', 'de']

// Get initial language from localStorage
const getInitialLanguage = () => {
  if (typeof window !== 'undefined') {
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      return savedLanguage
    }
  }
  return 'ar' // Default to Arabic
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => getInitialLanguage())

  // Apply language to document
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language
      document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
      localStorage.setItem('language', language)
      // Force a re-render by dispatching a custom event
      window.dispatchEvent(new Event('languagechange'))
    }
  }, [language])

  const changeLanguage = React.useCallback((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguage(lang)
      localStorage.setItem('language', lang)
      console.log(`🌐 Language changed to: ${lang}`)

      // Sync to backend so API/push messages follow UI language (best-effort)
      const token = localStorage.getItem('token')
      if (token) {
        import('@/utils/httpClient')
          .then(({ default: httpClient }) =>
            httpClient.put('/notifications/preferences', { language: lang }).catch(() => {})
          )
          .catch(() => {})
      }
    }
  }, [])

  const value = useMemo(() => ({
    language,
    setLanguage: changeLanguage,
    isRTL: language === 'ar',
  }), [language, changeLanguage])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export default LanguageContext

